/**
 * SPA route table.
 *
 * Wrapped by <BrowserRouter> in `main.tsx` so that <AuthProvider> and the
 * <SessionExpiredModal> can share router primitives (`useNavigate`,
 * `useLocation`) with the route tree.
 *
 * Structure:
 *   /login              — public
 *   /accept-invite      — public, invite acceptance (also /invite/accept —
 *                         two backend services compose the link from two
 *                         different env vars, see AcceptInvitePage)
 *   /forgot-password    — public, password-reset request
 *   /reset-password     — public, password-reset confirm (link from email)
 *   /                   — auth-guarded AppShell layout, hosts nested routes
 *     index             — `<RoleHomeRedirect>` dispatches admins → /dashboard,
 *                         selectors → /selector/home
 *     /dashboard        — landing for authenticated admins
 *     /catalog ...      — admin sections (placeholders until later tasks)
 *     /selector/...     — selector cabinet, wrapped in `<SelectorOnlyRoute>`
 *                         so admins / clients get bounced back to /
 *     /__demo__         — shared component playground
 *   /404, /*            — not-found
 *
 * Auth guard: `<ProtectedRoute>` (a.k.a. RequireAuth) redirects unauthenticated
 * users via `<Navigate to="/login?return_to=…" replace />` (see
 * `protected-route.tsx` for the implementation).
 */

import { Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Center, Loader } from '@mantine/core';
import { AppShell } from '@/layouts/AppShell';
import { LoginPage } from '@/pages/login';
import { NotFoundPage } from '@/pages/not-found';
import { ProtectedRoute } from '@/lib/protected-route';
import { RoleHomeRedirect } from '@/lib/role-home-redirect';
import { SelectorOnlyRoute } from '@/lib/selector-route';
import { StaffOnlyRoute } from '@/lib/staff-route';
import { lazyWithReload } from '@/lib/lazy-with-reload';

const ComponentsDemo = lazyWithReload(() =>
  import('@/components/__demo__').then((mod) => ({ default: mod.ComponentsDemo })),
);
const AcceptInvitePage = lazyWithReload(() =>
  import('@/pages/auth').then((mod) => ({ default: mod.AcceptInvitePage })),
);
const ForgotPasswordPage = lazyWithReload(() =>
  import('@/pages/auth').then((mod) => ({ default: mod.ForgotPasswordPage })),
);
const ResetPasswordPage = lazyWithReload(() =>
  import('@/pages/auth').then((mod) => ({ default: mod.ResetPasswordPage })),
);
const DashboardPage = lazyWithReload(() =>
  import('@/pages/dashboard').then((mod) => ({ default: mod.DashboardPage })),
);
const NotificationsPage = lazyWithReload(() =>
  import('@/pages/notifications').then((mod) => ({
    default: mod.NotificationsPage,
  })),
);
const CatalogPage = lazyWithReload(() =>
  import('@/pages/catalog').then((mod) => ({ default: mod.CatalogPage })),
);
const TrackCardPage = lazyWithReload(() =>
  import('@/pages/catalog/TrackCardPage').then((mod) => ({
    default: mod.TrackCardPage,
  })),
);
const ModerationPage = lazyWithReload(() =>
  import('@/pages/moderation').then((mod) => ({ default: mod.ModerationPage })),
);
const UploadPage = lazyWithReload(() =>
  import('@/pages/upload/UploadPage').then((mod) => ({
    default: mod.UploadPage,
  })),
);
const TagDictionaryPage = lazyWithReload(() =>
  import('@/pages/tag-dictionary').then((mod) => ({
    default: mod.TagDictionaryPage,
  })),
);
const AuditPage = lazyWithReload(() =>
  import('@/pages/audit').then((mod) => ({ default: mod.AuditPage })),
);
const TranscodingPage = lazyWithReload(() =>
  import('@/pages/transcoding').then((mod) => ({
    default: mod.TranscodingPage,
  })),
);
const SettingsPage = lazyWithReload(() =>
  import('@/pages/settings').then((mod) => ({ default: mod.SettingsPage })),
);
const TariffsPage = lazyWithReload(() =>
  import('@/pages/settings/tariffs').then((mod) => ({ default: mod.TariffsPage })),
);
const VenueFormatsPage = lazyWithReload(() =>
  import('@/pages/settings/venue-formats').then((mod) => ({
    default: mod.VenueFormatsPage,
  })),
);
const ScheduleTemplatePage = lazyWithReload(() =>
  import('@/pages/settings/venue-formats/schedule-template').then((mod) => ({
    default: mod.ScheduleTemplatePage,
  })),
);
const NotificationsSettingsTab = lazyWithReload(() =>
  import('@/pages/settings/notifications').then((mod) => ({
    default: mod.NotificationsTab,
  })),
);
const MoodCompatSettingsTab = lazyWithReload(() =>
  import('@/pages/settings/mood-compat').then((mod) => ({
    default: mod.MoodCompatTab,
  })),
);
const CustomFieldsSettingsPage = lazyWithReload(() =>
  import('@/pages/settings/custom-fields').then((mod) => ({
    default: mod.CustomFieldsPage,
  })),
);
const ReportTemplatesSettingsPage = lazyWithReload(() =>
  import('@/pages/settings/report-templates').then((mod) => ({
    default: mod.ReportTemplatesPage,
  })),
);
const UsersSettingsPage = lazyWithReload(() =>
  import('@/pages/settings/users').then((mod) => ({
    default: mod.UsersPage,
  })),
);
const SecuritySettingsPage = lazyWithReload(() =>
  import('@/pages/settings/security').then((mod) => ({
    default: mod.SecurityPage,
  })),
);
const AccountsPage = lazyWithReload(() =>
  import('@/pages/accounts/AccountsPage').then((mod) => ({
    default: mod.AccountsPage,
  })),
);
const AccountCardPage = lazyWithReload(() =>
  import('@/pages/accounts/AccountCardPage').then((mod) => ({
    default: mod.AccountCardPage,
  })),
);
const NewAccountPage = lazyWithReload(() =>
  import('@/pages/accounts/new').then((mod) => ({
    default: mod.NewAccountPage,
  })),
);
const PresetsPage = lazyWithReload(() =>
  import('@/pages/presets/PresetsPage').then((mod) => ({
    default: mod.PresetsPage,
  })),
);
const PresetCardPage = lazyWithReload(() =>
  import('@/pages/presets/PresetCardPage').then((mod) => ({
    default: mod.PresetCardPage,
  })),
);
const VenueCardPage = lazyWithReload(() =>
  import('@/pages/venues/VenueCardPage').then((mod) => ({
    default: mod.VenueCardPage,
  })),
);
const SelectorsPage = lazyWithReload(() =>
  import('@/pages/selectors').then((mod) => ({
    default: mod.SelectorsPage,
  })),
);
const SelectorCardPage = lazyWithReload(() =>
  import('@/pages/selectors/selector-card').then((mod) => ({
    default: mod.SelectorCardPage,
  })),
);
const AnalyticsPage = lazyWithReload(() =>
  import('@/pages/analytics').then((mod) => ({ default: mod.AnalyticsPage })),
);
const SelectorHomePage = lazyWithReload(() =>
  import('@/pages/selector/home').then((mod) => ({
    default: mod.SelectorHomePage,
  })),
);
const SelectorUploadPage = lazyWithReload(() =>
  import('@/pages/selector/upload').then((mod) => ({
    default: mod.SelectorUploadPage,
  })),
);
const SelectorMyTracksPage = lazyWithReload(() =>
  import('@/pages/selector/tracks').then((mod) => ({
    default: mod.SelectorMyTracksPage,
  })),
);
const SelectorMyPresetsPage = lazyWithReload(() =>
  import('@/pages/selector/presets').then((mod) => ({
    default: mod.SelectorMyPresetsPage,
  })),
);
const SelectorMyPromosPage = lazyWithReload(() =>
  import('@/pages/selector/promos').then((mod) => ({
    default: mod.SelectorMyPromosPage,
  })),
);
const SelectorMyPayoutsPage = lazyWithReload(() =>
  import('@/pages/selector/payouts').then((mod) => ({
    default: mod.SelectorMyPayoutsPage,
  })),
);
const SelectorNotificationsPage = lazyWithReload(() =>
  import('@/pages/selector/notifications').then((mod) => ({
    default: mod.SelectorNotificationsPage,
  })),
);
const PromosPage = lazyWithReload(() =>
  import('@/pages/promos').then((mod) => ({ default: mod.PromosPage })),
);
const NewPromoPage = lazyWithReload(() =>
  import('@/pages/promos').then((mod) => ({ default: mod.NewPromoPage })),
);
const PromoCardPage = lazyWithReload(() =>
  import('@/pages/promos').then((mod) => ({ default: mod.PromoCardPage })),
);
const TicketsPage = lazyWithReload(() =>
  import('@/pages/tickets').then((mod) => ({ default: mod.TicketsPage })),
);
const TicketDetailPage = lazyWithReload(() =>
  import('@/pages/tickets/TicketDetailPage').then((mod) => ({
    default: mod.TicketDetailPage,
  })),
);
const TariffRequestsPage = lazyWithReload(() =>
  import('@/pages/tariff-requests').then((mod) => ({
    default: mod.TariffRequestsPage,
  })),
);

function RouteFallback() {
  return (
    <Center mih="60dvh">
      <Loader aria-label="Загрузка…" />
    </Center>
  );
}

export function AppRoutes() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/accept-invite" element={<AcceptInvitePage />} />
        <Route path="/invite/accept" element={<AcceptInvitePage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        <Route
          element={
            <ProtectedRoute>
              <AppShell />
            </ProtectedRoute>
          }
        >
          <Route index element={<RoleHomeRedirect />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          {/* Staff only (CEO, 2026-10-26); the card below is not, since a
              selector opens their tracks there from «Мои треки». */}
          <Route
            path="/catalog"
            element={
              <StaffOnlyRoute>
                <CatalogPage />
              </StaffOnlyRoute>
            }
          />
          <Route path="/catalog/upload" element={<UploadPage />} />
          <Route path="/catalog/tracks/:id" element={<TrackCardPage />} />
          <Route
            path="/moderation"
            element={
              <StaffOnlyRoute>
                <ModerationPage />
              </StaffOnlyRoute>
            }
          />
          <Route path="/presets" element={<PresetsPage />} />
          <Route path="/presets/:presetId" element={<PresetCardPage />} />
          <Route path="/tags" element={<Navigate to="/tag-dictionary" replace />} />
          <Route path="/tag-dictionary" element={<TagDictionaryPage />} />
          <Route path="/accounts" element={<AccountsPage />} />
          <Route path="/accounts/new" element={<NewAccountPage />} />
          <Route path="/accounts/:id" element={<AccountCardPage />} />
          <Route
            path="/accounts/:accountId/venues/:venueId"
            element={<VenueCardPage />}
          />
          <Route path="/selectors" element={<SelectorsPage />} />
          <Route path="/selectors/:id" element={<SelectorCardPage />} />
          <Route path="/promos" element={<PromosPage />} />
          <Route path="/promos/new" element={<NewPromoPage />} />
          <Route path="/promos/:id" element={<PromoCardPage />} />
          {/* Legacy path — redirect to canonical /promos. */}
          <Route path="/promo-codes" element={<Navigate to="/promos" replace />} />
          <Route path="/tickets" element={<TicketsPage />} />
          <Route path="/tickets/:id" element={<TicketDetailPage />} />
          <Route path="/tariff-requests" element={<TariffRequestsPage />} />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route path="/audit" element={<AuditPage />} />
          <Route
            path="/transcoding"
            element={
              <StaffOnlyRoute>
                <TranscodingPage />
              </StaffOnlyRoute>
            }
          />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/settings" element={<SettingsPage />}>
            <Route path="tariffs" element={<TariffsPage />} />
            <Route path="venue-formats" element={<VenueFormatsPage />} />
            {/* Subpage of «Категории заведений», not a new section — the
                sidebar keeps that item active (design §22.1). */}
            <Route
              path="venue-formats/:id/schedule-template"
              element={
                <StaffOnlyRoute>
                  <ScheduleTemplatePage />
                </StaffOnlyRoute>
              }
            />
            <Route path="notifications" element={<NotificationsSettingsTab />} />
            <Route path="mood-compat" element={<MoodCompatSettingsTab />} />
            <Route
              path="custom-fields"
              element={<CustomFieldsSettingsPage />}
            />
            <Route
              path="report-templates"
              element={<ReportTemplatesSettingsPage />}
            />
            <Route path="users" element={<UsersSettingsPage />} />
            {/* TASK-450: self-service 2FA — every admin-panel role, selectors
                included (SettingsPage gates the other tabs). */}
            <Route path="security" element={<SecuritySettingsPage />} />
            {/* BUG-023: legacy path → canonical /settings/users. */}
            <Route
              path="admins"
              element={<Navigate to="/settings/users" replace />}
            />
          </Route>

          <Route
            path="/selector"
            element={
              <SelectorOnlyRoute>
                <Navigate to="/selector/home" replace />
              </SelectorOnlyRoute>
            }
          />
          <Route
            path="/selector/home"
            element={
              <SelectorOnlyRoute>
                <SelectorHomePage />
              </SelectorOnlyRoute>
            }
          />
          <Route
            path="/selector/upload"
            element={
              <SelectorOnlyRoute>
                <SelectorUploadPage />
              </SelectorOnlyRoute>
            }
          />
          <Route
            path="/selector/tracks"
            element={
              <SelectorOnlyRoute>
                <SelectorMyTracksPage />
              </SelectorOnlyRoute>
            }
          />
          <Route
            path="/selector/presets"
            element={
              <SelectorOnlyRoute>
                <SelectorMyPresetsPage />
              </SelectorOnlyRoute>
            }
          />
          <Route
            path="/selector/promo-codes"
            element={
              <SelectorOnlyRoute>
                <SelectorMyPromosPage />
              </SelectorOnlyRoute>
            }
          />
          <Route
            path="/selector/payouts"
            element={
              <SelectorOnlyRoute>
                <SelectorMyPayoutsPage />
              </SelectorOnlyRoute>
            }
          />
          <Route
            path="/selector/notifications"
            element={
              <SelectorOnlyRoute>
                <SelectorNotificationsPage />
              </SelectorOnlyRoute>
            }
          />

          <Route path="/__demo__" element={<ComponentsDemo />} />
        </Route>

        <Route path="/404" element={<NotFoundPage />} />
        <Route path="*" element={<Navigate to="/404" replace />} />
      </Routes>
    </Suspense>
  );
}
